import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Plus, X } from "lucide-react";

const parseNums = (s) => String(s || "").split(",").map((x) => Number(x.trim())).filter(Number.isFinite);
const parseLabels = (s) => String(s || "").split(",").map((x) => x.trim());

// Items 28-30: numeric bins (row level) and measure bands (post-aggregation, at the
// visual's grain) usable as dimensions in rows / columns / axes / legend.
export function BandsEditor({ config, patchConfig, numericFields = [], disp = (x) => x }) {
  const dims = config.dimensions || [];
  const measures = config.measures || [];
  const mKeyOf = (m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
  const mLabelOf = (m) => m.label || m.key || `${m.agg || "sum"} ${m.field}`;
  const entries = dims.map((d, i) => ({ d, i })).filter(({ d }) => d && (d.band || d.bin));
  const upd = (i, patch) => patchConfig({ dimensions: dims.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const remove = (i) => patchConfig({ dimensions: dims.filter((_, j) => j !== i) });

  const addBin = () => {
    const f = numericFields[0]?.name;
    if (!f) return;
    patchConfig({ dimensions: [...dims, { field: f, bin: { size: 100 }, alias: `${f}_bin`, label: `${disp(f)} (bins)` }] });
  };
  const addBand = () => {
    const m = measures[0];
    if (!m) return;
    const k = mKeyOf(m);
    patchConfig({ dimensions: [...dims, { band: { measure: k, breaks: [], labels: [] }, alias: `${k}_band`, label: `${mLabelOf(m)} band` }] });
  };

  return (
    <div data-testid="bands-editor" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><Layers className="w-3 h-3" />Bands &amp; binning</Label>
        <span className="flex gap-1">
          <Button data-testid="bands-add-bin" size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={!numericFields.length} onClick={addBin}><Plus className="w-3 h-3 mr-0.5" />Bin field</Button>
          <Button data-testid="bands-add-band" size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={!measures.length} onClick={addBand}><Plus className="w-3 h-3 mr-0.5" />Band measure</Button>
        </span>
      </div>
      {!entries.length && (
        <p className="text-[10px] text-[color:var(--ink-3)]">
          <b>Bin field</b> groups raw numeric values into ranges before aggregation. <b>Band measure</b> labels each aggregated result (High / Medium / Low…) at this visual&apos;s grain — both act as dimensions.
        </p>
      )}
      {entries.map(({ d, i }) => (
        <div key={i} data-testid={`band-entry-${i}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <span className={"text-[9px] font-bold px-1.5 py-0.5 rounded " + (d.band ? "bg-[color:rgba(255,107,53,0.14)] text-[#FF6B35]" : "bg-[color:rgba(43,78,255,0.12)] text-[color:var(--brand-ocean)]")}>{d.band ? "BAND" : "BIN"}</span>
            {d.band ? (
              <Select value={d.band.measure || ""} onValueChange={(v) => upd(i, { band: { ...d.band, measure: v }, alias: `${v}_band` })}>
                <SelectTrigger data-testid={`band-measure-${i}`} className="bg-[color:var(--surface-2)] flex-1 min-w-0 h-7 text-xs"><SelectValue placeholder="Measure" /></SelectTrigger>
                <SelectContent>{measures.map((m) => <SelectItem key={mKeyOf(m)} value={mKeyOf(m)}>{mLabelOf(m)}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Select value={d.field || ""} onValueChange={(v) => upd(i, { field: v, alias: `${v}_bin` })}>
                <SelectTrigger data-testid={`bin-field-${i}`} className="bg-[color:var(--surface-2)] flex-1 min-w-0 h-7 text-xs"><SelectValue placeholder="Numeric field" /></SelectTrigger>
                <SelectContent>{numericFields.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-[color:var(--ink-3)]" onClick={() => remove(i)}><X className="w-3.5 h-3.5" /></Button>
          </div>
          {!d.band && (
            <div className="flex items-center gap-1.5">
              <Label className="text-[10px] text-[color:var(--ink-3)] shrink-0 w-16">Bin size</Label>
              <Input data-testid={`bin-size-${i}`} type="number" value={d.bin?.size ?? ""} placeholder="e.g. 100"
                onChange={(e) => upd(i, { bin: { ...d.bin, size: e.target.value === "" ? "" : Number(e.target.value) } })}
                className="bg-[color:var(--surface-2)] h-7 text-xs flex-1" />
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] text-[color:var(--ink-3)] shrink-0 w-16">Breaks</Label>
            <Input data-testid={`band-breaks-${i}`} value={(d.band ? d.band.breaks : d.bin?.breaks || []).join(", ")} placeholder="e.g. 1000, 5000"
              onChange={(e) => {
                const breaks = parseNums(e.target.value);
                d.band ? upd(i, { band: { ...d.band, breaks } }) : upd(i, { bin: { ...d.bin, breaks } });
              }}
              className="bg-[color:var(--surface-2)] h-7 text-xs flex-1" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] text-[color:var(--ink-3)] shrink-0 w-16">Labels</Label>
            <Input data-testid={`band-labels-${i}`} value={((d.band ? d.band.labels : d.bin?.labels) || []).join(", ")} placeholder="Low, Medium, High"
              onChange={(e) => {
                const labels = parseLabels(e.target.value);
                d.band ? upd(i, { band: { ...d.band, labels } }) : upd(i, { bin: { ...d.bin, labels } });
              }}
              className="bg-[color:var(--surface-2)] h-7 text-xs flex-1" />
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] text-[color:var(--ink-3)] shrink-0 w-16">Shown as</Label>
            <Input data-testid={`band-label-${i}`} value={d.label || ""} placeholder="Display name"
              onChange={(e) => upd(i, { label: e.target.value })}
              className="bg-[color:var(--surface-2)] h-7 text-xs flex-1" />
          </div>
          {d.band && <p className="text-[10px] text-[color:var(--ink-3)]">Evaluated after aggregation at this visual&apos;s other dimensions. N breaks create N+1 bands.</p>}
        </div>
      ))}
    </div>
  );
}
